import React from 'react';
import { ArrowRight, Briefcase, Buildings, Coins, Laptop, ListBullets, MapPin, SquaresFour, UsersThree } from '@phosphor-icons/react';
import type { HubCrossLink, HubCrossLinkKind } from '../../types/hub-cross-link';

export type { HubCrossLink, HubCrossLinkKind } from '../../types/hub-cross-link';

type Locale = 'en' | 'fi' | 'sv';

export interface HubCrossLinksProps {
  heading?: string;
  /** One line under the heading (cards variant only). */
  intro?: string;
  links: HubCrossLink[];
  /** `chips`: inline pills (headers, rails). `cards`: a tiled grid with kind labels and hints (job page, hub footers). */
  variant?: 'chips' | 'cards';
  locale?: Locale;
  className?: string;
  /** Heading element; the job page already has its H2s, hubs use H2. */
  headingLevel?: 'h2' | 'h3';
  id?: string;
}

const KIND_ICON: Record<HubCrossLinkKind, React.ComponentType<{ size?: number; weight?: 'regular' | 'fill' | 'duotone'; className?: string; 'aria-hidden'?: boolean }>> = {
  role: Briefcase,
  roleCity: MapPin,
  location: MapPin,
  employer: Buildings,
  employers: UsersThree,
  salary: Coins,
  category: SquaresFour,
  remote: Laptop,
  index: ListBullets,
};

/** Icon tile colour per kind: green for jobs (role, city), amber for money, brown for employers, slate for the rest. */
const KIND_TILE: Record<HubCrossLinkKind, string> = {
  role: 'bg-[#E4EEE7] text-[#2F4A38] dark:bg-[#1A2E22] dark:text-[#98CFA8]',
  roleCity: 'bg-[#E4EEE7] text-[#2F4A38] dark:bg-[#1A2E22] dark:text-[#98CFA8]',
  location: 'bg-[#E4EEE7] text-[#2F4A38] dark:bg-[#1A2E22] dark:text-[#98CFA8]',
  employer: 'bg-[#F5EDD0] text-[#6E4F00] dark:bg-[#3A2A0A] dark:text-[#E9C46A]',
  employers: 'bg-[#F5EDD0] text-[#6E4F00] dark:bg-[#3A2A0A] dark:text-[#E9C46A]',
  salary: 'bg-[#FBE9DA] text-[#8A4B12] dark:bg-[#3D2412] dark:text-[#F2B27A]',
  category: 'bg-[#E9E6F5] text-[#4A3F80] dark:bg-[#26213D] dark:text-[#B8AEE8]',
  remote: 'bg-[#E3EEF6] text-[#2C4F6E] dark:bg-[#1A2A38] dark:text-[#9CC6E6]',
  index: 'bg-[#F2EADF] text-[#3D3529] dark:bg-[#2A1D0F] dark:text-[#C4A882]',
};

/** Small kind label above each card title, so the tile reads as a page type at a glance. */
const KIND_LABEL: Record<Locale, Record<HubCrossLinkKind, string>> = {
  fi: { role: 'Ammatti', roleCity: 'Ammatti kaupungissa', location: 'Kaupunki', employer: 'Työnantaja', employers: 'Työnantajat', salary: 'Palkka', category: 'Toimiala', remote: 'Etätyö', index: 'Hakemisto' },
  sv: { role: 'Yrke', roleCity: 'Yrke i staden', location: 'Stad', employer: 'Arbetsgivare', employers: 'Arbetsgivare', salary: 'Lön', category: 'Bransch', remote: 'Distans', index: 'Register' },
  en: { role: 'Role', roleCity: 'Role in city', location: 'City', employer: 'Employer', employers: 'Employers', salary: 'Salary', category: 'Field', remote: 'Remote', index: 'Index' },
};

function formatCount(n: number, locale: Locale): string {
  return locale === 'en' ? n.toLocaleString('en-US') : n.toLocaleString('fi-FI').replace(/ /g, ' ');
}

/**
 * The one component every hub and the job page use for links to other indexable pages, so the
 * link graph looks and reads the same everywhere. Server-rendered, no intl dependency (callers
 * pass localized labels), plain anchors so crawlers and the static SRP shell both see them.
 */
export function HubCrossLinks({ heading, intro, links, variant = 'chips', locale = 'en', className = '', headingLevel = 'h2', id }: HubCrossLinksProps): React.ReactElement | null {
  if (!links || links.length === 0) return null;
  const Heading = headingLevel;
  const headingId = id ? `${id}-heading` : undefined;

  if (variant === 'cards') {
    return (
      <section
        id={id}
        className={`relative rounded-[28px] border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 bg-[#F2EADF] dark:bg-[#211609] px-4 py-6 sm:px-6 sm:py-7 lg:px-10 lg:py-9 ${className}`}
        aria-labelledby={headingId}
      >
        <div className="relative">
          {heading ? (
            <Heading id={headingId} className="text-xl sm:text-2xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF]">
              {heading}
            </Heading>
          ) : null}
          {intro ? <p className="mt-1 max-w-2xl text-sm text-[#7A6E5F] dark:text-[#B8A48C]">{intro}</p> : null}
          <ul className={`grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 ${heading || intro ? 'mt-5' : ''}`}>
            {links.map((link) => {
              const Icon = KIND_ICON[link.kind];
              return (
                <li key={link.href} className="min-w-0">
                  <a
                    href={link.href}
                    className="group relative flex h-full items-start gap-3.5 rounded-2xl border border-[#DDD5C8]/80 bg-white/85 px-4 py-4 shadow-sm transition-all duration-200 hover:border-[#3B5E45]/40 hover:bg-white hover:shadow-md motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45] dark:border-[#4A3520] dark:bg-[#1F1509]/90 dark:hover:border-[#98CFA8]/40 dark:hover:bg-[#2A1D0F]"
                  >
                    <span aria-hidden="true" className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl ${KIND_TILE[link.kind]}`}>
                      <Icon size={22} weight="duotone" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-[#7A6E5F] dark:text-[#B8A48C]">
                        {KIND_LABEL[locale][link.kind]}
                        {link.count != null && link.count > 0 ? (
                          <span className="ml-2 inline-flex items-center rounded-full bg-[#F2EADF] px-1.5 py-0.5 text-[10px] font-bold tabular-nums normal-case tracking-normal text-[#3D3529] dark:bg-[#2A1D0F] dark:text-[#C4A882]">
                            {formatCount(link.count, locale)}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-[15px] font-semibold leading-snug text-[#1E1A14] group-hover:text-[#2F4A38] dark:text-[#F2EADF] dark:group-hover:text-[#98CFA8]">
                        {link.label}
                      </span>
                      {link.hint ? <span className="mt-1 block text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">{link.hint}</span> : null}
                    </span>
                    <ArrowRight
                      aria-hidden="true"
                      size={16}
                      className="mt-1 flex-none text-[#3B5E45] opacity-0 transition-all duration-200 group-hover:opacity-100 motion-safe:-translate-x-1 motion-safe:group-hover:translate-x-0 dark:text-[#98CFA8]"
                    />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    );
  }

  return (
    <div id={id} className={className}>
      {heading ? (
        <Heading id={headingId} className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-2">
          {heading}
        </Heading>
      ) : null}
      <ul className="flex flex-wrap gap-2">
        {links.map((link) => {
          const Icon = KIND_ICON[link.kind];
          return (
            <li key={link.href}>
              <a
                href={link.href}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#DDD5C8] bg-white/70 px-3 py-1.5 text-sm text-[#1E1A14] transition-colors hover:border-[#3B5E45]/50 hover:bg-white dark:border-[#4A3520] dark:bg-[#1F1509]/80 dark:text-[#F2EADF] dark:hover:bg-[#2A1D0F]"
              >
                <Icon aria-hidden="true" size={14} weight="duotone" className="text-[#3B5E45] dark:text-[#98CFA8]" />
                {link.label}
                {link.count != null && link.count > 0 ? (
                  <span className="tabular-nums text-xs text-[#7A6E5F] dark:text-[#B8A48C]">({formatCount(link.count, locale)})</span>
                ) : null}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
